/*
 * SkylitStarTrail — the add-to-cart flourish: a comet leaves the button,
 * arcs across the screen trailing twinkling stars, and lands in the basket
 * icon with a small burst and a pop.
 *
 *   var flight = SkylitStarTrail.launch(buttonEl, [candidateTargets...], {
 *     onLand: function () {}   // e.g. bump the badge count
 *   });
 *   flight.land().then(navigate);   // resolves once the comet has landed
 *   flight.cancel();                // the add failed: fade out, no pop
 *
 * The first visible candidate is the target (the preview's bag on mobile,
 * the header's on desktop). Everything is drawn on one fixed, pointer-less
 * canvas that removes itself when the last particle dies. Motion is scaled by
 * elapsed time, so it runs at the same speed at 60 or 120Hz. Reduced motion,
 * or no visible target: no animation, land() resolves immediately.
 */
(function () {
  if (window.SkylitStarTrail) return;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var FLIGHT_MS = 820;
  var SETTLE_MS = 260;   // pop plays out before the caller navigates
  // Starlight on dark pages; on the light theme white sparkles vanish into
  // the cream panel, so the trail turns gold and amber instead.
  var DARK_COLOURS = ['255,255,255', '253,242,216', '209,162,113', '196,214,255', '255,226,170'];
  var LIGHT_COLOURS = ['196,140,74', '209,162,113', '168,112,52', '232,184,110', '140,96,160'];

  function injectStyles() {
    if (document.getElementById('sky-trail-styles')) return;
    var st = document.createElement('style');
    st.id = 'sky-trail-styles';
    st.textContent =
      '@keyframes sky-trail-pop{0%{transform:scale(1)}35%{transform:scale(1.24)}65%{transform:scale(0.94)}100%{transform:scale(1)}}' +
      '.sky-trail-pop{animation:sky-trail-pop 440ms cubic-bezier(.3,1.5,.5,1) both;}';
    document.head.appendChild(st);
  }

  function isVisible(el) {
    if (!el) return false;
    var r = el.getBoundingClientRect();
    if (!r.width || !r.height || r.bottom < 0 || r.top > window.innerHeight) return false;
    var cs = getComputedStyle(el);
    return cs.visibility !== 'hidden' && cs.display !== 'none' && parseFloat(cs.opacity) > 0.05;
  }

  function centre(el) {
    var r = el.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }

  function easeInOutCubic(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
  function bezier(p0, p1, p2, t) {
    var u = 1 - t;
    return { x: u * u * p0.x + 2 * u * t * p1.x + t * t * p2.x, y: u * u * p0.y + 2 * u * t * p1.y + t * t * p2.y };
  }

  function noop() { return { land: function () { return Promise.resolve(); }, cancel: function () {} }; }

  function launch(fromEl, targets, opts) {
    opts = opts || {};
    var list = Array.isArray(targets) ? targets : [targets];
    var to = null;
    for (var i = 0; i < list.length; i++) { if (isVisible(list[i])) { to = list[i]; break; } }
    if (reduceMotion || !fromEl || !to || !window.requestAnimationFrame) return noop();
    injectStyles();

    var light = document.documentElement.getAttribute('data-customizer-theme') !== 'dark';
    var COLOURS = light ? LIGHT_COLOURS : DARK_COLOURS;
    var HEAD = light
      ? { core: '255,236,196', mid: '214,160,90', edge: '196,140,74', spark: '150,98,40' }
      : { core: '255,250,236', mid: '255,226,170', edge: '209,162,113', spark: '255,255,255' };
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var W = window.innerWidth, H = window.innerHeight;
    var canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
    canvas.style.cssText = 'position:fixed;left:0;top:0;width:' + W + 'px;height:' + H + 'px;pointer-events:none;z-index:2147483000;';
    document.body.appendChild(canvas);
    var ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    var p0 = centre(fromEl);
    var p2 = centre(to);
    // Arc: rise from the button first, then sweep across into the bag — the
    // control point sits near the start's x and most of the way up.
    var p1 = {
      x: p0.x + (p2.x - p0.x) * 0.12 - Math.min(80, W * 0.12),
      y: p2.y + (p0.y - p2.y) * 0.18
    };

    var particles = [];
    var start = performance.now();
    var last = start;
    var spawnDebt = 0;
    var prevE = 0;
    var phase = 'flying';      // flying -> landed | cancelled
    var headAlpha = 1;
    var resolveArrived;
    var arrived = new Promise(function (r) { resolveArrived = r; });

    function spawn(x, y, burst) {
      var ang = Math.random() * Math.PI * 2;
      var speed = burst ? 0.06 + Math.random() * 0.16 : 0.004 + Math.random() * 0.03; // px per ms
      particles.push({
        x: x + (burst ? 0 : (Math.random() - 0.5) * 8),
        y: y + (burst ? 0 : (Math.random() - 0.5) * 8),
        vx: Math.cos(ang) * speed,
        vy: Math.sin(ang) * speed + (burst ? 0 : 0.01), // trail sinks a touch
        life: 0,
        max: burst ? 380 + Math.random() * 260 : 420 + Math.random() * 480,
        size: burst ? 0.9 + Math.random() * 2 : 0.6 + Math.random() * 2.2,
        colour: COLOURS[(Math.random() * COLOURS.length) | 0],
        tw: Math.random() * Math.PI * 2,
        twSpeed: 0.012 + Math.random() * 0.02
      });
    }

    function drawSparkle(x, y, s, a, colour) {
      // Four-point star: two thin crossed diamonds.
      ctx.fillStyle = 'rgba(' + colour + ',' + a + ')';
      ctx.beginPath();
      ctx.moveTo(x, y - s * 2.6); ctx.lineTo(x + s * 0.45, y); ctx.lineTo(x, y + s * 2.6); ctx.lineTo(x - s * 0.45, y); ctx.closePath();
      ctx.moveTo(x - s * 2.6, y); ctx.lineTo(x, y + s * 0.45); ctx.lineTo(x + s * 2.6, y); ctx.lineTo(x, y - s * 0.45); ctx.closePath();
      ctx.fill();
    }

    function drawHead(x, y, a) {
      var g = ctx.createRadialGradient(x, y, 0, x, y, 22);
      g.addColorStop(0, 'rgba(' + HEAD.core + ',' + a + ')');
      g.addColorStop(0.25, 'rgba(' + HEAD.mid + ',' + (0.75 * a) + ')');
      g.addColorStop(1, 'rgba(' + HEAD.edge + ',0)');
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.arc(x, y, 22, 0, Math.PI * 2); ctx.fill();
      drawSparkle(x, y, 3.8, a, HEAD.spark);
    }

    function frame(now) {
      var dt = Math.min(48, now - last); // clamp: a background tab must not teleport
      last = now;
      ctx.clearRect(0, 0, W, H);

      var t = Math.min(1, (now - start) / FLIGHT_MS);
      var e = easeInOutCubic(t);
      var head = bezier(p0, p1, p2, e);

      if (phase === 'flying') {
        // Mid-flight the head covers ~20px a frame; spread this frame's
        // particles along the stretch it travelled so the trail is unbroken.
        spawnDebt += dt * 0.28;
        var n = Math.floor(spawnDebt);
        for (var k = 1; k <= n; k++) {
          var q = bezier(p0, p1, p2, prevE + (e - prevE) * (k / n));
          spawn(q.x, q.y, false);
        }
        spawnDebt -= n;
        prevE = e;
        if (t >= 1) {
          phase = 'landed';
          for (var b = 0; b < 22; b++) spawn(p2.x, p2.y, true);
          to.classList.remove('sky-trail-pop');
          void to.offsetWidth;
          to.classList.add('sky-trail-pop');
          setTimeout(function () { to.classList.remove('sky-trail-pop'); }, 500);
          if (opts.onLand) { try { opts.onLand(); } catch (e) { /* noop */ } }
          resolveArrived(true);
        }
      } else if (phase === 'cancelled') {
        headAlpha = Math.max(0, headAlpha - dt / 220);
      }

      for (var i = particles.length - 1; i >= 0; i--) {
        var p = particles[i];
        p.life += dt;
        if (p.life >= p.max) { particles.splice(i, 1); continue; }
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vx *= Math.pow(0.996, dt);
        p.vy *= Math.pow(0.996, dt);
        p.tw += p.twSpeed * dt;
        var fade = 1 - p.life / p.max;
        var a = fade * (0.55 + 0.45 * Math.sin(p.tw));
        if (a <= 0.01) continue;
        if (p.size > 1.6) drawSparkle(p.x, p.y, p.size * 0.7, a, p.colour);
        else {
          ctx.fillStyle = 'rgba(' + p.colour + ',' + a + ')';
          ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2); ctx.fill();
        }
      }

      if (phase === 'flying' || (phase === 'cancelled' && headAlpha > 0)) drawHead(head.x, head.y, headAlpha);

      var finished = !particles.length && (phase === 'landed' || (phase === 'cancelled' && headAlpha <= 0));
      if (finished) {
        if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
        return;
      }
      requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);

    return {
      // Resolves once the comet is in the bag and the pop has played.
      land: function () {
        return arrived.then(function (ok) {
          if (!ok) return;
          return new Promise(function (r) { setTimeout(r, SETTLE_MS); });
        });
      },
      cancel: function () {
        if (phase !== 'flying') return;
        phase = 'cancelled';
        resolveArrived(false);
      }
    };
  }

  window.SkylitStarTrail = { launch: launch };
})();
